import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, rlsApi } from '@/lib/api';
import toast from 'react-hot-toast';

interface RLSPolicy {
  id: string;
  project_id: string;
  table_name: string;
  policy_name: string;
  policy_definition: string;
  policy_type: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'ALL';
  role_name?: string;
  created_at: string;
}

interface TableWithRLS {
  table_name: string;
  rls_enabled: boolean;
  policy_count: number;
}

export default function RLSPolicies() {
  const queryClient = useQueryClient();
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [newPolicy, setNewPolicy] = useState({
    table_name: '',
    policy_name: '',
    policy_definition: '',
    policy_type: 'SELECT' as const,
    role_name: 'authenticated'
  });

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch RLS policies for selected project
  const { data: policiesResponse, isLoading: policiesLoading } = useQuery(
    ['rls-policies', projectId],
    () => rlsApi.getPolicies(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch tables with RLS info for selected project
  const { data: tablesResponse } = useQuery(
    ['rls-tables', projectId],
    () => rlsApi.getTables(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Create policy mutation
  const createPolicyMutation = useMutation(
    (policyData: typeof newPolicy) => 
      rlsApi.createPolicy(projectId as string, policyData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rls-policies', projectId]);
        queryClient.invalidateQueries(['rls-tables', projectId]);
        setShowCreateModal(false);
        setNewPolicy({
          table_name: '',
          policy_name: '',
          policy_definition: '',
          policy_type: 'SELECT',
          role_name: 'authenticated'
        });
        toast.success('RLS policy created successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to create RLS policy');
      },
    }
  );

  // Delete policy mutation
  const deletePolicyMutation = useMutation(
    (policyId: string) => 
      rlsApi.deletePolicy(policyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rls-policies', projectId]);
        queryClient.invalidateQueries(['rls-tables', projectId]);
        setShowDeleteModal(null);
        toast.success('RLS policy deleted successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to delete RLS policy');
      },
    }
  );

  // Enable/Disable RLS for table
  const toggleRLSMutation = useMutation(
    ({ tableName, enable }: { tableName: string; enable: boolean }) => 
      rlsApi.toggleRLS(projectId as string, tableName, enable),
    {
      onSuccess: (_, { enable }) => {
        queryClient.invalidateQueries(['rls-tables', projectId]);
        toast.success(`RLS ${enable ? 'enabled' : 'disabled'} successfully`);
      },
      onError: (_error: any) => {
        toast.error('Failed to toggle RLS');
      },
    }
  );

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;
  const policies = policiesResponse?.data || [];
  const tables: TableWithRLS[] = tablesResponse?.data || [];

  return (
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Row Level Security Policies
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage PostgreSQL Row Level Security policies for {project?.name || 'this project'}.
            </p>
          </div>
        </div>

        <>
          {/* Tables with RLS Status */}
            <div className="mt-6 card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Tables & RLS Status</h2>
              </div>
              
              {tables.length === 0 ? (
                <p className="text-sm text-gray-500">No tables found in this project.</p>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Table Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          RLS Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Policies
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tables.map((table) => (
                        <tr key={table.table_name}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {table.table_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {table.rls_enabled ? (
                                <>
                                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                  <span className="text-sm text-green-800">Enabled</span>
                                </>
                              ) : (
                                <>
                                  <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                                  <span className="text-sm text-red-800">Disabled</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {table.policy_count} policies
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => toggleRLSMutation.mutate({
                                tableName: table.table_name,
                                enable: !table.rls_enabled
                              })}
                              disabled={toggleRLSMutation.isLoading}
                              className={`${
                                table.rls_enabled 
                                  ? 'text-red-600 hover:text-red-900' 
                                  : 'text-green-600 hover:text-green-900'
                              } disabled:opacity-50`}
                            >
                              {table.rls_enabled ? 'Disable RLS' : 'Enable RLS'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* RLS Policies */}
            <div className="mt-6 card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">RLS Policies</h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Policy
                </button>
              </div>

              {policiesLoading ? (
                <div className="animate-pulse space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-20 bg-gray-200 rounded"></div>
                  ))}
                </div>
              ) : policies.length === 0 ? (
                <div className="text-center py-12">
                  <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No RLS policies</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating your first RLS policy.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {policies.map((policy: RLSPolicy) => (
                    <div key={policy.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-sm font-medium text-gray-900">
                              {policy.policy_name}
                            </h3>
                            <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              policy.policy_type === 'SELECT' ? 'bg-blue-100 text-blue-800' :
                              policy.policy_type === 'INSERT' ? 'bg-green-100 text-green-800' :
                              policy.policy_type === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
                              policy.policy_type === 'DELETE' ? 'bg-red-100 text-red-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {policy.policy_type}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-600">
                            Table: <span className="font-mono">{policy.table_name}</span>
                          </p>
                          <p className="mt-1 text-sm text-gray-600">
                            Policy: <span className="font-mono text-xs">{policy.policy_definition}</span>
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Role: {policy.role_name || 'public'}
                          </p>
                        </div>
                        <button
                          onClick={() => setShowDeleteModal(policy.id)}
                          className="ml-4 text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </>

        {/* Create Policy Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create RLS Policy</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Table Name</label>
                    <select
                      value={newPolicy.table_name}
                      onChange={(e) => setNewPolicy({ ...newPolicy, table_name: e.target.value })}
                      className="mt-1 form-select w-full"
                    >
                      <option value="">Select table...</option>
                      {tables.map((table) => (
                        <option key={table.table_name} value={table.table_name}>
                          {table.table_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Policy Name</label>
                    <input
                      type="text"
                      value={newPolicy.policy_name}
                      onChange={(e) => setNewPolicy({ ...newPolicy, policy_name: e.target.value })}
                      className="mt-1 form-input w-full"
                      placeholder="e.g., Users can view own posts"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Policy Type</label>
                    <select
                      value={newPolicy.policy_type}
                      onChange={(e) => setNewPolicy({ ...newPolicy, policy_type: e.target.value as any })}
                      className="mt-1 form-select w-full"
                    >
                      <option value="SELECT">SELECT</option>
                      <option value="INSERT">INSERT</option>
                      <option value="UPDATE">UPDATE</option>
                      <option value="DELETE">DELETE</option>
                      <option value="ALL">ALL</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <input
                      type="text"
                      value={newPolicy.role_name}
                      onChange={(e) => setNewPolicy({ ...newPolicy, role_name: e.target.value })}
                      className="mt-1 form-input w-full"
                      placeholder="authenticated, public, or custom role"
                    />
                    <p className="mt-1 text-xs text-gray-500">Leave empty for all roles</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Policy Definition</label>
                    <textarea
                      value={newPolicy.policy_definition}
                      onChange={(e) => setNewPolicy({ ...newPolicy, policy_definition: e.target.value })}
                      className="mt-1 form-textarea w-full"
                      rows={3}
                      placeholder="e.g., user_id = auth.uid()"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => createPolicyMutation.mutate(newPolicy)}
                    disabled={createPolicyMutation.isLoading || !newPolicy.table_name || !newPolicy.policy_name || !newPolicy.policy_definition}
                    className="btn-primary disabled:opacity-50"
                  >
                    {createPolicyMutation.isLoading ? 'Creating...' : 'Create Policy'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">Delete RLS Policy</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete this RLS policy? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-center space-x-4 mt-4">
                  <button
                    onClick={() => setShowDeleteModal(null)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deletePolicyMutation.mutate(showDeleteModal)}
                    disabled={deletePolicyMutation.isLoading}
                    className="btn-danger disabled:opacity-50"
                  >
                    {deletePolicyMutation.isLoading ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}